import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { drApi } from "../../../services/drApi";

const STATUS_TABS = [
  { value: "",          label: "All" },
  { value: "PENDING",   label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
  { value: "BREACHED",  label: "Breached" },
  { value: "FAILED",    label: "Failed" },
];

const ESCALATION_LABEL = {
  EDUCATION_EMAIL: "Education email",
  ONE_ON_ONE_CALL: "1:1 call",
  BLOCKED:         "Blocked",
};

const ESCALATION_TONE = {
  EDUCATION_EMAIL: "blue",
  ONE_ON_ONE_CALL: "amber",
  BLOCKED:         "gray",
};

const STATUS_CLASS = {
  PENDING:   "s-rev",
  COMPLETED: "s-active",
  BREACHED:  "s-fail",
  CANCELLED: "s-mute",
};

function investorName(sla) {
  const inv = sla?.Investor;
  if (!inv) return `User #${sla.investor_user_id}`;
  const full = `${inv.firstname || ""} ${inv.lastname || ""}`.trim();
  return full || inv.email || `User #${inv.id}`;
}

function DRSuitabilityList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const isFailedTab = statusFilter === "FAILED";

    const promise = isFailedTab
      ? drApi.suitabilityFailures({ page, limit: 25 })
      : (() => {
          const params = { type: "SUITABILITY", page, limit: 25 };
          if (statusFilter) params.sla = statusFilter;
          return drApi.approvalsQueue(params);
        })();

    promise
      .then((res) => {
        if (!live) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [statusFilter, page]);

  const subtitle = (
    <>
      <span className="mono">{total}</span>
      <span>record{total === 1 ? "" : "s"}</span>
      <span>·</span>
      <span className="mono">page {page} of {Math.max(totalPages, 1)}</span>
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Suitability assessments" subtitle={subtitle} />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Centralized view of every suitability decision in the SLA queue. Row click opens the full investor detail + decision form.
      </p>

      <div className="toolbar">
        <div className="toolbar__pills">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              type="button"
              className={`toolbar__pill${statusFilter === t.value ? " toolbar__pill--on" : ""}`}
              onClick={() => { setStatusFilter(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No suitability records in this filter.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            {statusFilter === "FAILED" ? (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Investor</th>
                    <th>Attempt</th>
                    <th>Escalation</th>
                    <th>Reason</th>
                    <th>Final decision</th>
                    <th>Logged</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => {
                    const goToOutcome = () => {
                      if (f.call_schedule_id) {
                        navigate(`/dr/suitability/failed/${f.investment_id || f.investor_user_id}`);
                      }
                    };
                    return (
                      <tr key={f.id}>
                        <td>
                          <div>
                            {f.Investor
                              ? `${f.Investor.firstname || ""} ${f.Investor.lastname || ""}`.trim() || f.Investor.email || `User #${f.investor_user_id}`
                              : `User #${f.investor_user_id}`}
                          </div>
                          <div className="tbl-id">{f?.Investor?.email || "—"}</div>
                        </td>
                        <td className="mono">#{f.attempt_number}</td>
                        <td>
                          <Pill tone={ESCALATION_TONE[f.escalation_type] || "gray"}>
                            {ESCALATION_LABEL[f.escalation_type] || f.escalation_type}
                          </Pill>
                        </td>
                        <td className="muted" style={{ whiteSpace: "normal", maxWidth: 280 }}>
                          {f.failure_reason || "—"}
                        </td>
                        <td>{f.final_decision || "Open"}</td>
                        <td className="muted">{formatAuditTimestamp(f.createdAt)}</td>
                        <td className="r">
                          {f.call_schedule_id && !f.final_decision && (
                            <Btn variant="ghost" size="sm" onClick={goToOutcome}>
                              Call outcome <ChevronRight />
                            </Btn>
                          )}
                          {!f.call_schedule_id && f.escalation_type === "EDUCATION_EMAIL" && (
                            <span className="muted" style={{ fontSize: "var(--t-xs)" }}>Email sent</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Investor</th>
                    <th>Associated deal</th>
                    <th>SLA status</th>
                    <th>Risk</th>
                    <th>Decision</th>
                    <th>Submitted</th>
                    <th>Deadline</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((sla) => {
                    const openDetail = () => navigate(`/dr/approvals/${sla.id}`);
                    const statusUpper = String(sla.status || "").toUpperCase();
                    return (
                      <tr
                        key={sla.id}
                        tabIndex={0}
                        role="button"
                        onClick={openDetail}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDetail();
                          }
                        }}
                      >
                        <td>
                          <div>{investorName(sla)}</div>
                          <div className="tbl-id">{sla?.Investor?.email || "—"}</div>
                        </td>
                        <td className="muted">{sla.investment_id ? `Deal inv #${sla.investment_id}` : "—"}</td>
                        <td>
                          <span className={`tbl-status ${STATUS_CLASS[statusUpper] || "s-mute"}`}>
                            {sla.status}
                          </span>
                        </td>
                        <td className="muted">{sla?.Investor?.risk_level || "—"}</td>
                        <td>{sla.decision || "—"}</td>
                        <td className="muted">{formatAuditTimestamp(sla.submitted_at)}</td>
                        <td className="muted">{formatAuditTimestamp(sla.deadline_at)}</td>
                        <td className="r">
                          <Btn
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openDetail(); }}
                          >
                            Review <ChevronRight />
                          </Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default DRSuitabilityList;
